import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { CustomSelect } from "../components/ui/custom-select";
import { DatePicker } from "../components/ui/date-picker";
import { TimePicker } from "../components/ui/time-picker";
import PaymentManagementPanel from "../components/PaymentManagementPanel";
import { 
  RefreshCw, Calendar, MapPin, Users, Car, Download,
  Clock, Phone, Mail, CheckCircle, XCircle, AlertCircle,
  Filter, Euro, Search, Plus, Trash2, Edit3, Eye,
  Send, MessageSquare, ChevronDown, ChevronUp, X, Loader2
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function AdminBookingsPage() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stats, setStats] = useState(null);
  
  // Filters
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  
  // Modals
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  // Expanded rows
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Action states
  const [updating, setUpdating] = useState(false);
  const [actionError, setActionError] = useState("");

  const token = localStorage.getItem("adminToken");

  // Check authentication
  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
    }
  }, [navigate, token]);

  // Fetch bookings
  const fetchBookings = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      if (typeFilter !== "all") params.append("booking_type", typeFilter);
      if (statusFilter !== "all") params.append("booking_status", statusFilter);
      params.append("limit", "200");
      
      const response = await fetch(`${API_BASE}/api/bookings?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("adminToken");
          navigate("/admin/login");
          return;
        }
        throw new Error("Failed to fetch bookings");
      }
      
      const data = await response.json();
      setBookings(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, typeFilter, statusFilter, navigate]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE}/api/bookings/stats/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  }, [token]);

  useEffect(() => {
    fetchBookings();
    fetchStats();
  }, [fetchBookings, fetchStats]);

  // Filter bookings by search
  const filteredBookings = bookings.filter(b => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      b.booking_id?.toLowerCase().includes(search) ||
      b.name?.toLowerCase().includes(search) ||
      b.email?.toLowerCase().includes(search) ||
      b.phone?.toLowerCase().includes(search) ||
      b.from_name?.toLowerCase().includes(search) ||
      b.to_name?.toLowerCase().includes(search)
    );
  }).filter(b => {
    if (dateFrom && b.pickup_date < dateFrom) return false;
    if (dateTo && b.pickup_date > dateTo) return false;
    return true;
  });

  // Status change handler (for dropdown selects)
  const handleStatusChange = async (bookingId, field, value) => {
    setUpdating(true);
    setActionError("");
    try {
      const response = await fetch(`${API_BASE}/api/bookings/${bookingId}/admin-edit`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ [field]: value })
      });
      if (!response.ok) throw new Error(`Failed to update ${field}`);
      await fetchBookings();
      await fetchStats();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (bookingId) => {
    setUpdating(true);
    try {
      const response = await fetch(`${API_BASE}/api/bookings/${bookingId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error("Failed to delete");
      setShowDeleteConfirm(null);
      await fetchBookings();
      await fetchStats();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  const handleExportCSV = () => {
    const params = new URLSearchParams();
    if (statusFilter !== "all") params.append("booking_status", statusFilter);
    if (dateFrom) params.append("date_from", dateFrom);
    if (dateTo) params.append("date_to", dateTo);
    
    window.open(`${API_BASE}/api/bookings/export/csv?${params}&token=${token}`, "_blank");
  };

  const toggleRow = (bookingId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(bookingId)) {
      newExpanded.delete(bookingId);
    } else {
      newExpanded.add(bookingId);
    }
    setExpandedRows(newExpanded);
  };

  // Status badge component
  const StatusBadge = ({ status, type }) => {
    const styles = {
      booking: {
        pending: "bg-yellow-100 text-yellow-800",
        confirmed: "bg-green-100 text-green-800",
        cancelled: "bg-red-100 text-red-800",
      },
      payment: {
        unpaid: "bg-red-100 text-red-800",
        partial: "bg-yellow-100 text-yellow-800",
        paid: "bg-green-100 text-green-800",
        refunded: "bg-gray-100 text-gray-800",
      }
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[type]?.[status] || "bg-gray-100 text-gray-800"}`}>
        {status}
      </span>
    );
  };

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Bookings</h1>
            <p className="text-gray-500">Manage all transfer and tour bookings</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={handleExportCSV} variant="outline">
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
            <Button onClick={() => setShowCreateModal(true)} className="bg-teal-500 hover:bg-teal-600">
              <Plus className="h-4 w-4 mr-2" /> New Booking
            </Button>
          </div>
        </div>

        {/* Dashboard Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 shadow-sm border">
              <p className="text-sm text-gray-500">Total Bookings</p>
              <p className="text-2xl font-bold">{stats.total_bookings}</p>
            </div>
            <div className="bg-yellow-50 rounded-xl p-4 shadow-sm border border-yellow-200">
              <p className="text-sm text-yellow-700">Pending</p>
              <p className="text-2xl font-bold text-yellow-800">{stats.pending_count}</p>
            </div>
            <div className="bg-green-50 rounded-xl p-4 shadow-sm border border-green-200">
              <p className="text-sm text-green-700">Confirmed</p>
              <p className="text-2xl font-bold text-green-800">{stats.confirmed_count}</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-4 shadow-sm border border-blue-200">
              <p className="text-sm text-blue-700">Today's Pickups</p>
              <p className="text-2xl font-bold text-blue-800">{stats.todays_pickups}</p>
            </div>
            <div className="bg-purple-50 rounded-xl p-4 shadow-sm border border-purple-200">
              <p className="text-sm text-purple-700">Revenue</p>
              <p className="text-2xl font-bold text-purple-800">€{stats.total_revenue}</p>
            </div>
            <div className="bg-teal-50 rounded-xl p-4 shadow-sm border border-teal-200">
              <p className="text-sm text-orange-700">Pending Payments</p>
              <p className="text-2xl font-bold text-orange-800">€{stats.pending_payments}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm text-gray-500 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="ID, name, email, phone..."
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:border-teal-500"
                />
              </div>
            </div>
            <div className="w-40">
              <label className="block text-sm text-gray-500 mb-1">Type</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500"
              >
                <option value="all">All Types</option>
                <option value="transfer">Transfers</option>
                <option value="tour">Tours</option>
              </select>
            </div>
            <div className="w-40">
              <label className="block text-sm text-gray-500 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="w-40">
              <label className="block text-sm text-gray-500 mb-1">Date From</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500"
              />
            </div>
            <div className="w-40">
              <label className="block text-sm text-gray-500 mb-1">Date To</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500"
              />
            </div>
            <Button onClick={fetchBookings} variant="outline">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Error display */}
        {(error || actionError) && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-red-700">
            {error || actionError}
          </div>
        )}

        {/* Bookings Table */}
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="w-8 px-4 py-3"></th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Booking ID</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Customer</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Route/Tour</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Date & Time</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Booking Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Payment Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                      Loading bookings...
                    </td>
                  </tr>
                ) : filteredBookings.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                      No bookings found
                    </td>
                  </tr>
                ) : (
                  filteredBookings.map((booking) => (
                    <React.Fragment key={booking.booking_id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <button onClick={() => toggleRow(booking.booking_id)} className="text-gray-400 hover:text-gray-600">
                            {expandedRows.has(booking.booking_id) ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-mono font-medium text-teal-600">{booking.booking_id}</span>
                          <span className="block text-xs text-gray-400">
                            {booking.booking_type}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium text-gray-900">{booking.name}</p>
                          <p className="text-xs text-gray-500">{booking.email}</p>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm">
                            <span>{booking.from_name}</span>
                            <span className="text-teal-500 mx-1">→</span>
                            <span>{booking.to_name}</span>
                            {booking.has_return_trip && (
                              <span className="ml-2 text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">🔄 Return</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium">{booking.pickup_date}</p>
                          <p className="text-xs text-gray-500">{booking.pickup_time}</p>
                          {booking.has_return_trip && (
                            <p className="text-xs text-green-600">Return: {booking.return_date}</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                            booking.booking_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            booking.booking_status === 'confirmed' ? 'bg-green-100 text-green-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {booking.booking_status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                            booking.payment_status === 'unpaid' ? 'bg-red-100 text-red-800' :
                            booking.payment_status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                            booking.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                            booking.payment_status === 'overpaid' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {booking.payment_status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="font-medium">€{booking.total_price}</p>
                          <p className="text-xs text-green-600">€{booking.amount_paid || 0} paid</p>
                          {(booking.amount_refunded || 0) > 0 && (
                            <p className="text-xs text-teal-600">€{booking.amount_refunded} refunded</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1">
                            <button
                              onClick={() => setSelectedBooking(booking)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                              title="Edit"
                              data-testid={`edit-booking-${booking.booking_id}`}
                            >
                              <Edit3 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setShowDeleteConfirm(booking.booking_id)}
                              className="p-1.5 text-gray-400 hover:bg-gray-100 rounded"
                              title="Delete"
                              data-testid={`delete-booking-${booking.booking_id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {/* Expanded details row */}
                      {expandedRows.has(booking.booking_id) && (
                        <tr className="bg-gray-50">
                          <td colSpan="9" className="px-4 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div>
                                <h4 className="font-medium text-gray-700 mb-2">Contact Info</h4>
                                <div className="space-y-1 text-sm">
                                  <p><Mail className="h-3 w-3 inline mr-1" /> {booking.email}</p>
                                  <p><Phone className="h-3 w-3 inline mr-1" /> {booking.phone}</p>
                                </div>
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-700 mb-2">Outbound Trip</h4>
                                <div className="space-y-1 text-sm">
                                  <p><Car className="h-3 w-3 inline mr-1" /> {booking.vehicle_name}</p>
                                  <p><Users className="h-3 w-3 inline mr-1" /> {booking.passengers} passengers</p>
                                  {booking.flight_number && <p>✈️ Flight: {booking.flight_number}</p>}
                                  {booking.pickup_address && <p>📍 Pickup: {booking.pickup_address}</p>}
                                  {booking.dropoff_address && <p>📍 Dropoff: {booking.dropoff_address}</p>}
                                  {(booking.baby_seats > 0 || booking.child_seats > 0) && (
                                    <p>🪑 Child seats: {booking.baby_seats} baby, {booking.child_seats} child</p>
                                  )}
                                </div>
                              </div>
                              {booking.has_return_trip && (
                                <div>
                                  <h4 className="font-medium text-green-700 mb-2">🔄 Return Trip</h4>
                                  <div className="space-y-1 text-sm bg-green-50 p-2 rounded">
                                    <p><Calendar className="h-3 w-3 inline mr-1" /> {booking.return_date}</p>
                                    <p><Clock className="h-3 w-3 inline mr-1" /> {booking.return_time}</p>
                                    {booking.return_flight_number && <p>✈️ Flight: {booking.return_flight_number}</p>}
                                    <p className="text-green-600 font-medium">+€{booking.return_price || 0}</p>
                                  </div>
                                </div>
                              )}
                              <div>
                                <h4 className="font-medium text-gray-700 mb-2">Notes</h4>
                                <div className="space-y-1 text-sm">
                                  {booking.special_requests && (
                                    <p className="text-gray-600"><strong>Customer:</strong> {booking.special_requests}</p>
                                  )}
                                  {booking.admin_notes && (
                                    <p className="text-teal-600"><strong>Admin:</strong> {booking.admin_notes}</p>
                                  )}
                                  {!booking.special_requests && !booking.admin_notes && (
                                    <p className="text-gray-400">No notes</p>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="mt-3 pt-3 border-t text-xs text-gray-400">
                              Created: {new Date(booking.created_at).toLocaleString()} | 
                              Updated: {new Date(booking.updated_at).toLocaleString()}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 bg-gray-50 border-t text-sm text-gray-500">
            Showing {filteredBookings.length} of {bookings.length} bookings
          </div>
        </div>
      </div>

      {/* Edit Booking Modal */}
      {selectedBooking && (
        <EditBookingModal
          booking={selectedBooking}
          token={token}
          onClose={() => setSelectedBooking(null)}
          onSave={() => {
            setSelectedBooking(null);
            fetchBookings();
            fetchStats();
          }}
        />
      )}

      {/* Create Booking Modal */}
      {showCreateModal && (
        <CreateBookingModal
          token={token}
          onClose={() => setShowCreateModal(false)}
          onSave={() => {
            setShowCreateModal(false);
            fetchBookings();
            fetchStats();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Booking?</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to permanently delete booking <strong>{showDeleteConfirm}</strong>? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => handleDelete(showDeleteConfirm)}
                disabled={updating}
                className="bg-red-500 hover:bg-red-600"
              >
                {updating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Delete
              </Button>
              <Button onClick={() => setShowDeleteConfirm(null)} variant="outline">
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

// Edit Booking Modal Component
function EditBookingModal({ booking, token, onClose, onSave }) {
  const [activeTab, setActiveTab] = useState("details"); // details | payments
  
  // Change toggles - fields are frozen by default
  const [changeRouteEnabled, setChangeRouteEnabled] = useState(false);
  const [changeVehicleEnabled, setChangeVehicleEnabled] = useState(false);
  const [changePriceEnabled, setChangePriceEnabled] = useState(false);
  
  // Email sending state
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  
  const [formData, setFormData] = useState({
    booking_status: booking.booking_status,
    from_slug: booking.from_slug || "",
    to_slug: booking.to_slug || "",
    vehicle_id: booking.vehicle_id || "",
    pickup_date: booking.pickup_date,
    pickup_time: booking.pickup_time,
    passengers: booking.passengers,
    flight_number: booking.flight_number || "",
    pickup_address: booking.pickup_address || "",
    dropoff_address: booking.dropoff_address || "",
    baby_seats: booking.baby_seats || 0,
    child_seats: booking.child_seats || 0,
    name: booking.name,
    email: booking.email,
    phone: booking.phone,
    special_requests: booking.special_requests || "",
    admin_notes: booking.admin_notes || "",
    // Separate price fields for outbound and return
    one_way_price_override: booking.one_way_price || booking.total_price || 0,
    return_price_override: booking.return_price || 0,
    // Return trip fields
    has_return_trip: booking.has_return_trip || false,
    return_date: booking.return_date || "",
    return_time: booking.return_time || "",
    return_flight_number: booking.return_flight_number || ""
  });
  const [routes, setRoutes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  
  // Overpayment detection
  const [showOverpaymentWarning, setShowOverpaymentWarning] = useState(false);
  const [overpaymentAmount, setOverpaymentAmount] = useState(0);
  const [pendingSave, setPendingSave] = useState(null);

  // Original values from when booking was first created (never change)
  const originalFromName = booking.original_from_name || booking.from_name;
  const originalToName = booking.original_to_name || booking.to_name;
  const originalVehicleName = booking.original_vehicle_name || booking.vehicle_name;
  const originalHasReturnTrip = booking.original_has_return_trip ?? booking.has_return_trip;
  const originalOneWayPrice = booking.original_one_way_price ?? booking.one_way_price ?? booking.total_price;
  const originalReturnPrice = booking.original_return_price ?? 0;
  const originalTotalPrice = booking.original_total_price ?? booking.total_price;

  // Calculate effective total price based on current form values
  const calculatedTotalPrice = formData.one_way_price_override + (formData.has_return_trip ? formData.return_price_override : 0);

  // Determine route type for conditional fields
  // Use formData slugs if route change is enabled, otherwise use booking slugs
  const activeFromSlug = changeRouteEnabled ? formData.from_slug : booking.from_slug;
  const activeToSlug = changeRouteEnabled ? formData.to_slug : booking.to_slug;
  const isFromAirport = activeFromSlug?.includes('airport') || false;
  const isToAirport = activeToSlug?.includes('airport') || false;
  // Return trip starts from where outbound ended
  const returnIsFromAirport = isToAirport;

  // Get today's date in YYYY-MM-DD format for date validation (using local date)
  const getTodayLocal = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const today = getTodayLocal();

  // Validation function for Edit Modal
  const validateForm = () => {
    const errors = {};
    
    // Route validation (if route change enabled)
    if (changeRouteEnabled) {
      if (!formData.from_slug) errors.from_slug = "Pickup location is required";
      if (!formData.to_slug) errors.to_slug = "Destination is required";
    }
    
    // Vehicle validation (if vehicle change enabled)
    if (changeVehicleEnabled && !formData.vehicle_id) {
      errors.vehicle_id = "Vehicle is required";
    }
    
    // Always required fields
    if (!formData.passengers || formData.passengers < 1) errors.passengers = "At least 1 passenger required";
    if (!formData.pickup_date) errors.pickup_date = "Pickup date is required";
    if (!formData.pickup_time) errors.pickup_time = "Pickup time is required";
    if (!formData.name || formData.name.trim().length < 2) errors.name = "Name must be at least 2 characters";
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = "Valid email is required";
    if (!formData.phone || formData.phone.trim().length < 5) errors.phone = "Phone must be at least 5 characters";
    
    // Conditional fields based on route
    if (isFromAirport && !formData.flight_number?.trim()) {
      errors.flight_number = "Flight number is required for airport pickup";
    }
    if (!isFromAirport && !formData.pickup_address?.trim()) {
      errors.pickup_address = "Pickup address is required";
    }
    if (!isToAirport && !formData.dropoff_address?.trim()) {
      errors.dropoff_address = "Drop-off address is required";
    }
    
    // Return trip validation
    if (formData.has_return_trip) {
      if (!formData.return_date) errors.return_date = "Return date is required";
      if (formData.return_date && formData.pickup_date && formData.return_date <= formData.pickup_date) {
        errors.return_date = "Return date must be after pickup date";
      }
      if (!formData.return_time) errors.return_time = "Return time is required";
      // Return flight number required if return is FROM airport
      if (returnIsFromAirport && !formData.return_flight_number?.trim()) {
        errors.return_flight_number = "Return flight number is required";
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Scroll to first error
  const scrollToFirstError = (errors) => {
    const errorFields = Object.keys(errors);
    if (errorFields.length > 0) {
      const firstErrorField = document.querySelector(`[data-field="${errorFields[0]}"]`);
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  // Fetch routes and vehicles
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [routesRes, vehiclesRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/routes?limit=100`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vehicles`)
        ]);
        if (routesRes.ok) setRoutes(await routesRes.json());
        if (vehiclesRes.ok) setVehicles(await vehiclesRes.json());
      } catch (err) {
        console.error("Failed to fetch data:", err);
      }
    };
    fetchData();
  }, []);

  const getEffectivePrice = () => {
    if (changePriceEnabled) {
      // Use the calculated total from separate price fields
      return calculatedTotalPrice;
    }
    return booking.total_price;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError("");
    
    // Validate form first
    if (!validateForm()) {
      scrollToFirstError(validationErrors);
      return;
    }
    
    const effectivePrice = getEffectivePrice();
    const amountPaid = booking.amount_paid || 0;
    
    // Capture current toggle states
    const toggleStates = {
      changeRoute: changeRouteEnabled,
      changeVehicle: changeVehicleEnabled,
      changePrice: changePriceEnabled
    };
    
    // Check for overpayment
    if (effectivePrice < amountPaid) {
      setOverpaymentAmount(amountPaid - effectivePrice);
      setShowOverpaymentWarning(true);
      setPendingSave({ formData: { ...formData }, toggleStates });
      return;
    }
    
    await doSave(formData, toggleStates);
  };

  const doSave = async (dataToSave, options = {}) => {
    setSaving(true);
    setError("");
    
    // Use passed options or current state values
    const shouldChangeRoute = options.changeRoute ?? changeRouteEnabled;
    const shouldChangeVehicle = options.changeVehicle ?? changeVehicleEnabled;
    const shouldChangePrice = options.changePrice ?? changePriceEnabled;

    try {
      // Build the submit data - only include changed fields
      const submitData = {
        booking_status: dataToSave.booking_status,
        pickup_date: dataToSave.pickup_date,
        pickup_time: dataToSave.pickup_time,
        passengers: dataToSave.passengers,
        flight_number: dataToSave.flight_number,
        pickup_address: dataToSave.pickup_address,
        dropoff_address: dataToSave.dropoff_address,
        baby_seats: dataToSave.baby_seats,
        child_seats: dataToSave.child_seats,
        name: dataToSave.name,
        email: dataToSave.email,
        phone: dataToSave.phone,
        special_requests: dataToSave.special_requests,
        admin_notes: dataToSave.admin_notes,
        // Return trip fields
        has_return_trip: dataToSave.has_return_trip,
        return_date: dataToSave.return_date || null,
        return_time: dataToSave.return_time || null,
        return_flight_number: dataToSave.return_flight_number || null
      };
      
      // Only include route change if enabled
      if (shouldChangeRoute) {
        submitData.from_slug = dataToSave.from_slug;
        submitData.to_slug = dataToSave.to_slug;
      }
      
      // Only include vehicle change if enabled
      if (shouldChangeVehicle) {
        submitData.vehicle_id = dataToSave.vehicle_id;
      }
      
      // Only include price changes if enabled - send separate price overrides
      if (shouldChangePrice) {
        submitData.one_way_price_override = dataToSave.one_way_price_override;
        if (dataToSave.has_return_trip) {
          submitData.return_price_override = dataToSave.return_price_override;
        }
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings/${booking.booking_id}/admin-edit`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to update booking");
      }

      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWithoutRefund = () => {
    setShowOverpaymentWarning(false);
    doSave(pendingSave.formData, pendingSave.toggleStates);
  };

  const handleIssueRefund = async () => {
    // First save the booking
    await doSave(pendingSave.formData, pendingSave.toggleStates);
    // Switch to payments tab to issue refund
    setShowOverpaymentWarning(false);
    setActiveTab("payments");
  };

  // Send booking link email to customer
  const handleSendBookingLink = async () => {
    setSendingEmail(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings/${booking.booking_id}/send-booking-link`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      
      if (res.ok) {
        setEmailSent(true);
        setTimeout(() => setEmailSent(false), 3000);
      } else {
        const data = await res.json();
        setError(data.detail || "Failed to send email");
      }
    } catch (err) {
      setError("Failed to send email");
    } finally {
      setSendingEmail(false);
    }
  };

  // Get unique from locations
  const fromLocations = [...new Set(routes.map(r => JSON.stringify({ slug: r.location_slug, name: r.location_name })))].map(s => JSON.parse(s));
  const selectedLocation = routes.find(r => r.location_slug === formData.from_slug);
  const destinations = selectedLocation?.routes || [];
  
  // Get selected vehicle's max passengers
  const selectedVehicle = vehicles.find(v => v.vehicle_id === formData.vehicle_id);
  const maxPassengers = selectedVehicle?.passengers || 7;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-3xl my-8 shadow-2xl">
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-xl font-bold">Manage Booking</h2>
            <p className="text-sm text-gray-500">ID: {booking.booking_id} • {booking.from_name} → {booking.to_name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab("details")}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === "details"
                ? "text-teal-600 border-b-2 border-teal-500"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Edit3 className="h-4 w-4 inline mr-2" />
            Booking Details
          </button>
          <button
            onClick={() => setActiveTab("payments")}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === "payments"
                ? "text-teal-600 border-b-2 border-teal-500"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Euro className="h-4 w-4 inline mr-2" />
            Payments & Transactions
          </button>
        </div>

        {/* Tab Content */}
        <div className="max-h-[60vh] overflow-y-auto">
          {activeTab === "details" ? (
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Booking Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Booking Status</label>
                <select
                  value={formData.booking_status}
                  onChange={(e) => setFormData({...formData, booking_status: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500"
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>

              {/* Route Section */}
              <div className="bg-gray-50 rounded-lg p-4 border">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-gray-500 uppercase tracking-wide">current route</span>
                  <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={changeRouteEnabled}
                      onChange={(e) => setChangeRouteEnabled(e.target.checked)}
                      className="rounded border-gray-300 text-teal-500 focus:ring-teal-500"
                    />
                    Change route
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">From</label>
                    <select
                      value={formData.from_slug}
                      onChange={(e) => setFormData({...formData, from_slug: e.target.value, to_slug: ""})}
                      disabled={!changeRouteEnabled}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500 text-sm ${!changeRouteEnabled ? 'bg-gray-100 text-gray-600' : ''}`}
                    >
                      <option value="">Select pickup...</option>
                      {fromLocations.map(loc => (
                        <option key={loc.slug} value={loc.slug}>{loc.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">To</label>
                    <select
                      value={formData.to_slug}
                      onChange={(e) => setFormData({...formData, to_slug: e.target.value})}
                      disabled={!changeRouteEnabled || !formData.from_slug}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500 text-sm ${!changeRouteEnabled ? 'bg-gray-100 text-gray-600' : ''}`}
                    >
                      <option value="">Select destination...</option>
                      {destinations.map(dest => (
                        <option key={dest.to_slug} value={dest.to_slug}>{dest.to_name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  originally booked: {originalFromName} → {originalToName}
                </p>
              </div>

              {/* Vehicle Section */}
              <div className="bg-gray-50 rounded-lg p-4 border">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-gray-500 uppercase tracking-wide">current vehicle</span>
                  <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={changeVehicleEnabled}
                      onChange={(e) => setChangeVehicleEnabled(e.target.checked)}
                      className="rounded border-gray-300 text-teal-500 focus:ring-teal-500"
                    />
                    Change vehicle
                  </label>
                </div>
                <select
                  value={formData.vehicle_id}
                  onChange={(e) => {
                    const newVehicle = vehicles.find(v => v.vehicle_id === e.target.value);
                    const newMaxPassengers = newVehicle?.passengers || 7;
                    setFormData({
                      ...formData, 
                      vehicle_id: e.target.value,
                      passengers: Math.min(formData.passengers, newMaxPassengers)
                    });
                  }}
                  disabled={!changeVehicleEnabled}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500 ${!changeVehicleEnabled ? 'bg-gray-100 text-gray-600' : ''}`}
                >
                  <option value="">Select vehicle...</option>
                  {vehicles.map(v => (
                    <option key={v.vehicle_id} value={v.vehicle_id}>{v.type} (max {v.passengers})</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-2">
                  originally booked: {originalVehicleName}
                </p>
              </div>

              {/* Price Section */}
              <div className="bg-gray-50 rounded-lg p-4 border">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="text-xs text-gray-500 uppercase tracking-wide">current price</span>
                    <p className="text-2xl font-bold text-gray-900">€{booking.total_price}</p>
                    {booking.has_return_trip && (
                      <p className="text-xs text-gray-500">
                        (€{booking.one_way_price || booking.total_price} outbound + €{booking.return_price || 0} return)
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={changePriceEnabled}
                        onChange={(e) => setChangePriceEnabled(e.target.checked)}
                        className="rounded border-gray-300 text-teal-500 focus:ring-teal-500"
                      />
                      Change price
                    </label>
                    <div className="text-right">
                      <span className="text-xs text-gray-500 uppercase tracking-wide">paid</span>
                      <p className="text-xl font-semibold text-green-600">€{booking.amount_paid || 0}</p>
                    </div>
                  </div>
                </div>
                {changePriceEnabled && (
                  <div className="bg-teal-50 rounded-lg p-3 border border-teal-200 mb-3 space-y-3">
                    {/* Outbound Price */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Outbound price:</span>
                      <div className="flex items-center gap-1">
                        <span className="text-gray-500 font-medium">€</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.one_way_price_override || 0}
                          onChange={(e) => setFormData({...formData, one_way_price_override: parseFloat(e.target.value) || 0})}
                          className="w-24 px-2 py-1 border rounded-lg focus:outline-none focus:border-teal-500 font-bold text-lg"
                        />
                      </div>
                    </div>
                    
                    {/* Return Price - only if has return trip */}
                    {formData.has_return_trip && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700">Return price:</span>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500 font-medium">€</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.return_price_override || 0}
                            onChange={(e) => setFormData({...formData, return_price_override: parseFloat(e.target.value) || 0})}
                            className="w-24 px-2 py-1 border rounded-lg focus:outline-none focus:border-teal-500 font-bold text-lg"
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Calculated Total */}
                    <div className="flex items-center justify-between pt-2 border-t border-teal-200">
                      <span className="text-sm font-semibold text-gray-800">New Total:</span>
                      <span className="text-xl font-bold text-teal-600">€{calculatedTotalPrice.toFixed(2)}</span>
                    </div>
                  </div>
                )}
                <p className="text-xs text-gray-400">
                  originally booked: €{originalTotalPrice}
                  {originalHasReturnTrip && ` (€${originalOneWayPrice} outbound + €${originalReturnPrice} return)`}
                </p>
              </div>

              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-4">
                <div data-field="pickup_date">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Date *</label>
                  <input
                    type="date"
                    value={formData.pickup_date}
                    onChange={(e) => { setFormData({...formData, pickup_date: e.target.value}); setValidationErrors(prev => ({...prev, pickup_date: undefined})); }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500 ${validationErrors.pickup_date ? 'border-red-500' : ''}`}
                  />
                  {validationErrors.pickup_date && <p className="text-red-500 text-xs mt-1">{validationErrors.pickup_date}</p>}
                </div>
                <div data-field="pickup_time">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Time *</label>
                  <input
                    type="time"
                    value={formData.pickup_time}
                    onChange={(e) => { setFormData({...formData, pickup_time: e.target.value}); setValidationErrors(prev => ({...prev, pickup_time: undefined})); }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500 ${validationErrors.pickup_time ? 'border-red-500' : ''}`}
                  />
                  {validationErrors.pickup_time && <p className="text-red-500 text-xs mt-1">{validationErrors.pickup_time}</p>}
                </div>
              </div>

              {/* Passengers & Seats */}
              <div className="grid grid-cols-4 gap-4">
                <div data-field="passengers">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Passengers *</label>
                  <input
                    type="number"
                    min="1"
                    max={maxPassengers}
                    value={formData.passengers}
                    onChange={(e) => setFormData({...formData, passengers: Math.min(parseInt(e.target.value) || 1, maxPassengers)})}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">max {maxPassengers}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Baby Seats</label>
                  <input
                    type="number"
                    min="0"
                    max="3"
                    value={formData.baby_seats}
                    onChange={(e) => setFormData({...formData, baby_seats: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Child Seats</label>
                  <input
                    type="number"
                    min="0"
                    max="3"
                    value={formData.child_seats}
                    onChange={(e) => setFormData({...formData, child_seats: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500"
                  />
                </div>
                {/* Flight # - only show if FROM airport */}
                {isFromAirport && (
                  <div data-field="flight_number">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Flight # *</label>
                    <input
                      type="text"
                      value={formData.flight_number}
                      onChange={(e) => { setFormData({...formData, flight_number: e.target.value}); setValidationErrors(prev => ({...prev, flight_number: undefined})); }}
                      placeholder="AB1234"
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500 ${validationErrors.flight_number ? 'border-red-500' : ''}`}
                    />
                    {validationErrors.flight_number && <p className="text-red-500 text-xs mt-1">{validationErrors.flight_number}</p>}
                  </div>
                )}
              </div>

              {/* Conditional Address Fields */}
              <div className="grid grid-cols-2 gap-4">
                {/* Pickup Address - only show if NOT from airport */}
                {!isFromAirport && (
                  <div data-field="pickup_address">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Address *</label>
                    <input
                      type="text"
                      value={formData.pickup_address}
                      onChange={(e) => { setFormData({...formData, pickup_address: e.target.value}); setValidationErrors(prev => ({...prev, pickup_address: undefined})); }}
                      placeholder="Hotel name, street address..."
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500 ${validationErrors.pickup_address ? 'border-red-500' : ''}`}
                    />
                    {validationErrors.pickup_address && <p className="text-red-500 text-xs mt-1">{validationErrors.pickup_address}</p>}
                  </div>
                )}
                {/* Dropoff Address - only show if NOT to airport */}
                {!isToAirport && (
                  <div data-field="dropoff_address">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Drop-off Address *</label>
                    <input
                      type="text"
                      value={formData.dropoff_address}
                      onChange={(e) => { setFormData({...formData, dropoff_address: e.target.value}); setValidationErrors(prev => ({...prev, dropoff_address: undefined})); }}
                      placeholder="Hotel name, street address..."
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500 ${validationErrors.dropoff_address ? 'border-red-500' : ''}`}
                    />
                  </div>
                )}
              </div>

              {/* Return Trip Section */}
              <div className={`p-4 rounded-lg border-2 ${formData.has_return_trip ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex items-center justify-between mb-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.has_return_trip}
                      onChange={(e) => setFormData({...formData, has_return_trip: e.target.checked})}
                      className="w-4 h-4 text-green-600 rounded"
                    />
                    <span className="font-medium text-gray-700">🔄 Return Trip</span>
                  </label>
                  {formData.has_return_trip && (
                    <span className="text-sm text-green-600 font-medium">+€{formData.return_price_override || booking.return_price || booking.one_way_price || 0}</span>
                  )}
                </div>
                
                {formData.has_return_trip && (
                  <div className="grid grid-cols-3 gap-3 mt-3">
                    <div data-field="return_date">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Return Date *</label>
                      <input
                        type="date"
                        value={formData.return_date}
                        min={formData.pickup_date}
                        onChange={(e) => { setFormData({...formData, return_date: e.target.value}); setValidationErrors(prev => ({...prev, return_date: undefined})); }}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green-400 ${validationErrors.return_date ? 'border-red-500' : ''}`}
                      />
                      {validationErrors.return_date && <p className="text-red-500 text-xs mt-1">{validationErrors.return_date}</p>}
                    </div>
                    <div data-field="return_time">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Return Time *</label>
                      <input
                        type="time"
                        value={formData.return_time}
                        onChange={(e) => { setFormData({...formData, return_time: e.target.value}); setValidationErrors(prev => ({...prev, return_time: undefined})); }}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green-400 ${validationErrors.return_time ? 'border-red-500' : ''}`}
                      />
                      {validationErrors.return_time && <p className="text-red-500 text-xs mt-1">{validationErrors.return_time}</p>}
                    </div>
                    {/* Return Flight # - only show if return is FROM airport (original was City → Airport) */}
                    {returnIsFromAirport && (
                      <div data-field="return_flight_number">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Return Flight # *</label>
                        <input
                          type="text"
                          value={formData.return_flight_number}
                          onChange={(e) => { setFormData({...formData, return_flight_number: e.target.value}); setValidationErrors(prev => ({...prev, return_flight_number: undefined})); }}
                          placeholder="AB5678"
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green-400 ${validationErrors.return_flight_number ? 'border-red-500' : ''}`}
                        />
                        {validationErrors.return_flight_number && <p className="text-red-500 text-xs mt-1">{validationErrors.return_flight_number}</p>}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div data-field="name">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => { setFormData({...formData, name: e.target.value}); setValidationErrors(prev => ({...prev, name: undefined})); }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500 ${validationErrors.name ? 'border-red-500' : ''}`}
                  />
                  {validationErrors.name && <p className="text-red-500 text-xs mt-1">{validationErrors.name}</p>}
                </div>
                <div data-field="phone">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => { setFormData({...formData, phone: e.target.value}); setValidationErrors(prev => ({...prev, phone: undefined})); }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500 ${validationErrors.phone ? 'border-red-500' : ''}`}
                  />
                  {validationErrors.phone && <p className="text-red-500 text-xs mt-1">{validationErrors.phone}</p>}
                </div>
              </div>

              <div data-field="email">
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => { setFormData({...formData, email: e.target.value}); setValidationErrors(prev => ({...prev, email: undefined})); }}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500 ${validationErrors.email ? 'border-red-500' : ''}`}
                />
                {validationErrors.email && <p className="text-red-500 text-xs mt-1">{validationErrors.email}</p>}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Special Requests (Customer)</label>
                <textarea
                  value={formData.special_requests}
                  onChange={(e) => setFormData({...formData, special_requests: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Notes (Internal)</label>
                <textarea
                  value={formData.admin_notes}
                  onChange={(e) => setFormData({...formData, admin_notes: e.target.value})}
                  rows={2}
                  placeholder="Internal notes not visible to customer..."
                  className="w-full px-3 py-2 border rounded-lg bg-teal-50 focus:outline-none focus:border-teal-500"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                  {error}
                </div>
              )}
            </form>
          ) : (
            <div className="p-6">
              <PaymentManagementPanel 
                booking={booking} 
                token={token} 
                onUpdate={onSave}
              />
            </div>
          )}
        </div>

        {/* Footer - only show save button on details tab */}
        <div className="flex gap-3 p-6 border-t">
          {activeTab === "details" && (
            <>
              <Button type="submit" onClick={handleSubmit} disabled={saving} className="bg-teal-500 hover:bg-teal-600">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
              <Button 
                onClick={handleSendBookingLink} 
                disabled={sendingEmail || emailSent}
                variant="outline"
                className={emailSent ? "border-green-500 text-green-600" : ""}
              >
                {sendingEmail ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : emailSent ? (
                  <CheckCircle className="h-4 w-4 mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                {emailSent ? "Email Sent!" : "Send Booking Link"}
              </Button>
            </>
          )}
          <Button onClick={onClose} variant="outline">Close</Button>
        </div>
      </div>

      {/* Overpayment Warning Modal */}
      {showOverpaymentWarning && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Overpayment Detected</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Customer has paid <strong>€{booking.amount_paid || 0}</strong> but the new total is <strong>€{getEffectivePrice()}</strong>.
                </p>
              </div>
            </div>
            
            <div className="bg-teal-50 rounded-lg p-4 mb-4 text-center">
              <p className="text-sm text-orange-700">Overpayment Amount</p>
              <p className="text-3xl font-bold text-teal-600">€{overpaymentAmount}</p>
            </div>
            
            <div className="space-y-2">
              <Button
                onClick={handleIssueRefund}
                className="w-full bg-teal-500 hover:bg-teal-600"
              >
                Issue €{overpaymentAmount} Refund
              </Button>
              <Button
                onClick={handleSaveWithoutRefund}
                variant="outline"
                className="w-full"
              >
                Keep as Credit
              </Button>
              <Button
                onClick={() => setShowOverpaymentWarning(false)}
                variant="outline"
                className="w-full text-gray-500"
              >
                Cancel Price Change
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Create Booking Modal Component
function CreateBookingModal({ token, onClose, onSave }) {
  const [formData, setFormData] = useState({
    booking_type: "transfer",
    from_slug: "",
    to_slug: "",
    vehicle_id: "",
    passengers: 2,
    pickup_date: "",
    pickup_time: "10:00",
    flight_number: "",
    pickup_address: "",
    dropoff_address: "",
    baby_seats: 0,
    child_seats: 0,
    name: "",
    email: "",
    phone: "",
    special_requests: "",
    admin_notes: "",
    booking_status: "confirmed",
    payment_status: "unpaid",
    amount_paid: 0,
    // Separate price overrides for outbound and return
    one_way_price_override: null,
    return_price_override: null,
    // Return trip fields
    has_return_trip: false,
    return_date: "",
    return_time: "10:00",
    return_flight_number: ""
  });
  const [routes, setRoutes] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  // Track calculated prices separately
  const [calculatedOneWayPrice, setCalculatedOneWayPrice] = useState(0);
  const [calculatedReturnPrice, setCalculatedReturnPrice] = useState(0);
  const [priceOverrideEnabled, setPriceOverrideEnabled] = useState(false);

  // Get today's date in YYYY-MM-DD format for min date validation (using local date)
  const getTodayLocalCreate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const today = getTodayLocalCreate();

  // Determine route type for conditional fields
  const isFromAirport = formData.from_slug?.includes('airport') || false;
  const isToAirport = formData.to_slug?.includes('airport') || false;
  // Return trip starts from where outbound ended
  const returnIsFromAirport = isToAirport;

  // Validation function
  const validateForm = () => {
    const errors = {};
    
    // Required fields
    if (!formData.from_slug) errors.from_slug = "Pickup location is required";
    if (!formData.to_slug) errors.to_slug = "Destination is required";
    if (!formData.vehicle_id) errors.vehicle_id = "Vehicle is required";
    if (!formData.passengers || formData.passengers < 1) errors.passengers = "At least 1 passenger required";
    if (!formData.pickup_date) errors.pickup_date = "Pickup date is required";
    if (formData.pickup_date && formData.pickup_date < today) errors.pickup_date = "Pickup date cannot be in the past";
    if (!formData.pickup_time) errors.pickup_time = "Pickup time is required";
    if (!formData.name || formData.name.trim().length < 2) errors.name = "Name must be at least 2 characters";
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = "Valid email is required";
    if (!formData.phone || formData.phone.trim().length < 5) errors.phone = "Phone must be at least 5 characters";
    
    // Conditional fields based on route
    if (isFromAirport && !formData.flight_number?.trim()) {
      errors.flight_number = "Flight number is required for airport pickup";
    }
    if (!isFromAirport && !formData.pickup_address?.trim()) {
      errors.pickup_address = "Pickup address is required";
    }
    if (!isToAirport && !formData.dropoff_address?.trim()) {
      errors.dropoff_address = "Drop-off address is required";
    }
    
    // Return trip validation
    if (formData.has_return_trip) {
      if (!formData.return_date) errors.return_date = "Return date is required";
      if (formData.return_date && formData.pickup_date && formData.return_date <= formData.pickup_date) {
        errors.return_date = "Return date must be after pickup date";
      }
      if (!formData.return_time) errors.return_time = "Return time is required";
      // Return flight number required if return is FROM airport (outbound was TO airport)
      if (returnIsFromAirport && !formData.return_flight_number?.trim()) {
        errors.return_flight_number = "Return flight number is required";
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Scroll to first error
  const scrollToFirstError = (errors) => {
    const errorFields = Object.keys(errors);
    if (errorFields.length > 0) {
      const firstErrorField = document.querySelector(`[data-field="${errorFields[0]}"]`);
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  // Fetch routes and vehicles
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [routesRes, vehiclesRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/routes?limit=100`),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vehicles`)
        ]);
        
        if (routesRes.ok) {
          const routesData = await routesRes.json();
          setRoutes(routesData);
        }
        if (vehiclesRes.ok) {
          const vehiclesData = await vehiclesRes.json();
          setVehicles(vehiclesData);
          // Set default vehicle if available
          if (vehiclesData.length > 0) {
            setFormData(prev => ({ ...prev, vehicle_id: vehiclesData[0].vehicle_id }));
          }
        }
      } catch (err) {
        console.error("Failed to fetch data:", err);
      }
    };
    fetchData();
  }, []);

  // Calculate price when route or vehicle changes
  useEffect(() => {
    if (!formData.from_slug || !formData.to_slug || !formData.vehicle_id) {
      setCalculatedOneWayPrice(0);
      setCalculatedReturnPrice(0);
      return;
    }

    // Find the selected route
    const selectedLocation = routes.find(r => r.location_slug === formData.from_slug);
    const selectedRoute = selectedLocation?.routes?.find(r => r.to_slug === formData.to_slug);
    
    if (selectedRoute) {
      // Find vehicle price from route's vehicle_prices
      const vehiclePrice = selectedRoute.vehicle_prices?.find(vp => vp.vehicle_id === formData.vehicle_id);
      const oneWayPrice = vehiclePrice?.price || selectedRoute.base_price || 0;
      setCalculatedOneWayPrice(oneWayPrice);
      // Return price is same as outbound
      setCalculatedReturnPrice(formData.has_return_trip ? oneWayPrice : 0);
    } else {
      setCalculatedOneWayPrice(0);
      setCalculatedReturnPrice(0);
    }
  }, [formData.from_slug, formData.to_slug, formData.vehicle_id, formData.has_return_trip, routes]);

  // Auto-update payment_status based on amount_paid vs total price
  const updatePaymentStatus = (amountPaid, totalPrice) => {
    if (amountPaid <= 0) {
      return "unpaid";
    } else if (amountPaid >= totalPrice) {
      return "paid";
    } else {
      return "partial";
    }
  };

  // Handle amount paid change with auto payment status update
  const handleAmountPaidChange = (value) => {
    const amountPaid = parseInt(value) || 0;
    const newPaymentStatus = updatePaymentStatus(amountPaid, effectiveTotalPrice);
    setFormData(prev => ({ 
      ...prev, 
      amount_paid: amountPaid,
      payment_status: newPaymentStatus
    }));
  };

  // Handle price override toggle
  const handlePriceOverrideToggle = (enabled) => {
    setPriceOverrideEnabled(enabled);
    if (enabled) {
      setFormData(prev => ({ 
        ...prev, 
        one_way_price_override: calculatedOneWayPrice,
        return_price_override: calculatedReturnPrice
      }));
    } else {
      setFormData(prev => ({ 
        ...prev, 
        one_way_price_override: null,
        return_price_override: null
      }));
    }
  };

  // Handle individual price override changes
  const handleOneWayPriceChange = (value) => {
    const newPrice = parseFloat(value) || 0;
    const returnPrice = formData.return_price_override || 0;
    const newTotal = newPrice + (formData.has_return_trip ? returnPrice : 0);
    const newPaymentStatus = updatePaymentStatus(formData.amount_paid, newTotal);
    setFormData(prev => ({ 
      ...prev, 
      one_way_price_override: newPrice,
      payment_status: newPaymentStatus
    }));
  };

  const handleReturnPriceChange = (value) => {
    const newPrice = parseFloat(value) || 0;
    const oneWayPrice = formData.one_way_price_override || 0;
    const newTotal = oneWayPrice + newPrice;
    const newPaymentStatus = updatePaymentStatus(formData.amount_paid, newTotal);
    setFormData(prev => ({ 
      ...prev, 
      return_price_override: newPrice,
      payment_status: newPaymentStatus
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // Validate form
    if (!validateForm()) {
      scrollToFirstError(validationErrors);
      return;
    }
    
    setSaving(true);

    try {
      const submitData = { ...formData };
      // Only include price overrides if enabled
      if (!priceOverrideEnabled) {
        delete submitData.one_way_price_override;
        delete submitData.return_price_override;
      } else {
        // If return trip is not enabled, don't send return price
        if (!formData.has_return_trip) {
          delete submitData.return_price_override;
        }
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings/admin/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to create booking");
      }

      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Get unique from locations
  const fromLocations = [...new Set(routes.map(r => JSON.stringify({ slug: r.location_slug, name: r.location_name })))].map(s => JSON.parse(s));
  
  // Get destinations for selected from location - routes are inside location.routes array
  const selectedLocation = routes.find(r => r.location_slug === formData.from_slug);
  const destinations = selectedLocation?.routes || [];

  // Get effective prices for display
  const effectiveOneWayPrice = priceOverrideEnabled && formData.one_way_price_override !== null 
    ? formData.one_way_price_override 
    : calculatedOneWayPrice;
  const effectiveReturnPrice = priceOverrideEnabled && formData.return_price_override !== null 
    ? formData.return_price_override 
    : calculatedReturnPrice;
  const effectiveTotalPrice = effectiveOneWayPrice + (formData.has_return_trip ? effectiveReturnPrice : 0);
  // For backwards compatibility - calculatedPrice is total
  const calculatedPrice = calculatedOneWayPrice + (formData.has_return_trip ? calculatedReturnPrice : 0);
  const effectivePrice = effectiveTotalPrice;

  // Get selected vehicle's max passengers
  const selectedVehicle = vehicles.find(v => v.vehicle_id === formData.vehicle_id);
  const maxPassengers = selectedVehicle?.passengers || 7;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-2xl my-8">
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h2 className="text-xl font-bold">Create New Booking</h2>
            <p className="text-sm text-gray-500">Manual booking entry</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Route Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div data-field="from_slug">
              <label className="block text-sm font-medium text-gray-700 mb-1">From *</label>
              <select
                value={formData.from_slug}
                onChange={(e) => { setFormData({...formData, from_slug: e.target.value, to_slug: ""}); setValidationErrors(prev => ({...prev, from_slug: undefined})); }}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500 ${validationErrors.from_slug ? 'border-red-500' : ''}`}
                required
              >
                <option value="">Select pickup...</option>
                {fromLocations.map(loc => (
                  <option key={loc.slug} value={loc.slug}>{loc.name}</option>
                ))}
              </select>
              {validationErrors.from_slug && <p className="text-red-500 text-xs mt-1">{validationErrors.from_slug}</p>}
            </div>
            <div data-field="to_slug">
              <label className="block text-sm font-medium text-gray-700 mb-1">To *</label>
              <select
                value={formData.to_slug}
                onChange={(e) => { setFormData({...formData, to_slug: e.target.value}); setValidationErrors(prev => ({...prev, to_slug: undefined})); }}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500 ${validationErrors.to_slug ? 'border-red-500' : ''}`}
                required
                disabled={!formData.from_slug}
              >
                <option value="">Select destination...</option>
                {destinations.map(dest => (
                  <option key={dest.to_slug} value={dest.to_slug}>{dest.to_name}</option>
                ))}
              </select>
              {validationErrors.to_slug && <p className="text-red-500 text-xs mt-1">{validationErrors.to_slug}</p>}
            </div>
          </div>

          {/* Vehicle */}
          <div data-field="vehicle_id">
            <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle *</label>
            <select
              value={formData.vehicle_id}
              onChange={(e) => {
                const newVehicle = vehicles.find(v => v.vehicle_id === e.target.value);
                const newMaxPassengers = newVehicle?.passengers || 7;
                setFormData({
                  ...formData, 
                  vehicle_id: e.target.value,
                  // Adjust passengers if exceeds new vehicle capacity
                  passengers: Math.min(formData.passengers, newMaxPassengers)
                });
                setValidationErrors(prev => ({...prev, vehicle_id: undefined}));
              }}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500 ${validationErrors.vehicle_id ? 'border-red-500' : ''}`}
              required
            >
              <option value="">Select vehicle...</option>
              {vehicles.map(v => (
                <option key={v.vehicle_id} value={v.vehicle_id}>{v.type} (max {v.passengers})</option>
              ))}
            </select>
            {validationErrors.vehicle_id && <p className="text-red-500 text-xs mt-1">{validationErrors.vehicle_id}</p>}
          </div>

          {/* Real-time Price Display */}
          {formData.from_slug && formData.to_slug && formData.vehicle_id && (
            <div className="bg-gray-50 rounded-lg p-4 border">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm text-gray-600">Calculated Price</p>
                  <p className="text-2xl font-bold text-gray-900">€{calculatedPrice}</p>
                  {formData.has_return_trip && (
                    <p className="text-xs text-gray-500">(€{calculatedOneWayPrice} outbound + €{calculatedReturnPrice} return)</p>
                  )}
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={priceOverrideEnabled}
                    onChange={(e) => handlePriceOverrideToggle(e.target.checked)}
                    className="rounded border-gray-300 text-teal-500 focus:ring-teal-500"
                  />
                  Override price
                </label>
              </div>
              {priceOverrideEnabled && (
                <div className="bg-teal-50 rounded-lg p-3 border border-teal-200 space-y-3">
                  {/* Outbound Price */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Outbound price:</span>
                    <div className="flex items-center gap-1">
                      <span className="text-gray-500 font-medium">€</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.one_way_price_override || 0}
                        onChange={(e) => handleOneWayPriceChange(e.target.value)}
                        className="w-24 px-2 py-1 border rounded-lg focus:outline-none focus:border-teal-500 font-bold text-lg"
                      />
                    </div>
                  </div>
                  
                  {/* Return Price - only if has return trip */}
                  {formData.has_return_trip && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">Return price:</span>
                      <div className="flex items-center gap-1">
                        <span className="text-gray-500 font-medium">€</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={formData.return_price_override || 0}
                          onChange={(e) => handleReturnPriceChange(e.target.value)}
                          className="w-24 px-2 py-1 border rounded-lg focus:outline-none focus:border-teal-500 font-bold text-lg"
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Calculated Total */}
                  <div className="flex items-center justify-between pt-2 border-t border-teal-200">
                    <span className="text-sm font-semibold text-gray-800">New Total:</span>
                    <span className="text-xl font-bold text-teal-600">€{effectiveTotalPrice.toFixed(2)}</span>
                  </div>
                  
                  {effectiveTotalPrice !== calculatedPrice && (
                    <p className="text-xs text-teal-600">
                      ⚠️ Price override active (original: €{calculatedPrice})
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div data-field="pickup_date">
              <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Date *</label>
              <input
                type="date"
                value={formData.pickup_date}
                min={today}
                onChange={(e) => { setFormData({...formData, pickup_date: e.target.value}); setValidationErrors(prev => ({...prev, pickup_date: undefined})); }}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500 ${validationErrors.pickup_date ? 'border-red-500' : ''}`}
                required
              />
              {validationErrors.pickup_date && <p className="text-red-500 text-xs mt-1">{validationErrors.pickup_date}</p>}
            </div>
            <div data-field="pickup_time">
              <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Time *</label>
              <input
                type="time"
                value={formData.pickup_time}
                onChange={(e) => { setFormData({...formData, pickup_time: e.target.value}); setValidationErrors(prev => ({...prev, pickup_time: undefined})); }}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500 ${validationErrors.pickup_time ? 'border-red-500' : ''}`}
                required
              />
              {validationErrors.pickup_time && <p className="text-red-500 text-xs mt-1">{validationErrors.pickup_time}</p>}
            </div>
          </div>

          {/* Return Trip Section */}
          <div className={`p-4 rounded-lg border-2 ${formData.has_return_trip ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
            <div className="flex items-center justify-between mb-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.has_return_trip}
                  onChange={(e) => setFormData({...formData, has_return_trip: e.target.checked})}
                  className="w-4 h-4 text-green-600 rounded"
                />
                <span className="font-medium text-gray-700">🔄 Add Return Trip</span>
              </label>
              {formData.has_return_trip && calculatedOneWayPrice > 0 && (
                <span className="text-sm text-green-600 font-medium">+€{calculatedReturnPrice}</span>
              )}
            </div>
            
            {formData.has_return_trip && (
              <div className="grid grid-cols-3 gap-3 mt-3">
                <div data-field="return_date">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Return Date *</label>
                  <input
                    type="date"
                    value={formData.return_date}
                    min={formData.pickup_date || today}
                    onChange={(e) => { setFormData({...formData, return_date: e.target.value}); setValidationErrors(prev => ({...prev, return_date: undefined})); }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green-400 ${validationErrors.return_date ? 'border-red-500' : ''}`}
                    required={formData.has_return_trip}
                  />
                  {validationErrors.return_date && <p className="text-red-500 text-xs mt-1">{validationErrors.return_date}</p>}
                </div>
                <div data-field="return_time">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Return Time *</label>
                  <input
                    type="time"
                    value={formData.return_time}
                    onChange={(e) => { setFormData({...formData, return_time: e.target.value}); setValidationErrors(prev => ({...prev, return_time: undefined})); }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green-400 ${validationErrors.return_time ? 'border-red-500' : ''}`}
                    required={formData.has_return_trip}
                  />
                  {validationErrors.return_time && <p className="text-red-500 text-xs mt-1">{validationErrors.return_time}</p>}
                </div>
                {/* Return Flight # - only show if return is FROM airport (original was City → Airport) */}
                {returnIsFromAirport && (
                  <div data-field="return_flight_number">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Return Flight # *</label>
                    <input
                      type="text"
                      value={formData.return_flight_number}
                      onChange={(e) => { setFormData({...formData, return_flight_number: e.target.value}); setValidationErrors(prev => ({...prev, return_flight_number: undefined})); }}
                      placeholder="AB5678"
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-green-400 ${validationErrors.return_flight_number ? 'border-red-500' : ''}`}
                    />
                    {validationErrors.return_flight_number && <p className="text-red-500 text-xs mt-1">{validationErrors.return_flight_number}</p>}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Passengers & Details */}
          <div className="grid grid-cols-4 gap-4">
            <div data-field="passengers">
              <label className="block text-sm font-medium text-gray-700 mb-1">Passengers</label>
              <input
                type="number"
                min="1"
                max={maxPassengers}
                value={formData.passengers}
                onChange={(e) => { setFormData({...formData, passengers: Math.min(parseInt(e.target.value) || 1, maxPassengers)}); setValidationErrors(prev => ({...prev, passengers: undefined})); }}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500 ${validationErrors.passengers ? 'border-red-500' : ''}`}
              />
              {validationErrors.passengers ? <p className="text-red-500 text-xs mt-1">{validationErrors.passengers}</p> : <p className="text-xs text-gray-400 mt-1">max {maxPassengers}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Baby Seats</label>
              <input
                type="number"
                min="0"
                max="3"
                value={formData.baby_seats}
                onChange={(e) => setFormData({...formData, baby_seats: parseInt(e.target.value) || 0})}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Child Seats</label>
              <input
                type="number"
                min="0"
                max="3"
                value={formData.child_seats}
                onChange={(e) => setFormData({...formData, child_seats: parseInt(e.target.value) || 0})}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500"
              />
            </div>
            {/* Flight # - only show if FROM airport */}
            {isFromAirport && (
              <div data-field="flight_number">
                <label className="block text-sm font-medium text-gray-700 mb-1">Flight # *</label>
                <input
                  type="text"
                  value={formData.flight_number}
                  onChange={(e) => { setFormData({...formData, flight_number: e.target.value}); setValidationErrors(prev => ({...prev, flight_number: undefined})); }}
                  placeholder="AB1234"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500 ${validationErrors.flight_number ? 'border-red-500' : ''}`}
                />
                {validationErrors.flight_number && <p className="text-red-500 text-xs mt-1">{validationErrors.flight_number}</p>}
              </div>
            )}
          </div>

          {/* Conditional Address Fields - only show when locations are selected */}
          {formData.from_slug && formData.to_slug && (!isFromAirport || !isToAirport) && (
            <div className="grid grid-cols-2 gap-4">
              {/* Pickup Address - only show if NOT from airport */}
              {!isFromAirport && (
                <div data-field="pickup_address">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Address *</label>
                  <input
                    type="text"
                    value={formData.pickup_address}
                    onChange={(e) => { setFormData({...formData, pickup_address: e.target.value}); setValidationErrors(prev => ({...prev, pickup_address: undefined})); }}
                    placeholder="Hotel name, street address..."
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500 ${validationErrors.pickup_address ? 'border-red-500' : ''}`}
                  />
                  {validationErrors.pickup_address && <p className="text-red-500 text-xs mt-1">{validationErrors.pickup_address}</p>}
                </div>
              )}
              {/* Dropoff Address - only show if NOT to airport */}
              {!isToAirport && (
                <div data-field="dropoff_address">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Drop-off Address *</label>
                  <input
                    type="text"
                    value={formData.dropoff_address}
                    onChange={(e) => { setFormData({...formData, dropoff_address: e.target.value}); setValidationErrors(prev => ({...prev, dropoff_address: undefined})); }}
                    placeholder="Hotel name, street address..."
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500 ${validationErrors.dropoff_address ? 'border-red-500' : ''}`}
                  />
                  {validationErrors.dropoff_address && <p className="text-red-500 text-xs mt-1">{validationErrors.dropoff_address}</p>}
                </div>
              )}
            </div>
          )}

          {/* Contact Info */}
          <div className="grid grid-cols-2 gap-4">
            <div data-field="name">
              <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => { setFormData({...formData, name: e.target.value}); setValidationErrors(prev => ({...prev, name: undefined})); }}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500 ${validationErrors.name ? 'border-red-500' : ''}`}
                required
              />
              {validationErrors.name && <p className="text-red-500 text-xs mt-1">{validationErrors.name}</p>}
            </div>
            <div data-field="phone">
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => { setFormData({...formData, phone: e.target.value}); setValidationErrors(prev => ({...prev, phone: undefined})); }}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500 ${validationErrors.phone ? 'border-red-500' : ''}`}
                required
              />
              {validationErrors.phone && <p className="text-red-500 text-xs mt-1">{validationErrors.phone}</p>}
            </div>
          </div>

          <div data-field="email">
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => { setFormData({...formData, email: e.target.value}); setValidationErrors(prev => ({...prev, email: undefined})); }}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500 ${validationErrors.email ? 'border-red-500' : ''}`}
              required
            />
            {validationErrors.email && <p className="text-red-500 text-xs mt-1">{validationErrors.email}</p>}
          </div>

          {/* Status & Payment - Improved Matrix */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
            <h4 className="text-sm font-medium text-blue-800 mb-3">Payment & Status</h4>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Booking Status</label>
                <select
                  value={formData.booking_status}
                  onChange={(e) => setFormData({...formData, booking_status: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500"
                >
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount Paid (€)</label>
                <input
                  type="number"
                  min="0"
                  max={effectivePrice}
                  value={formData.amount_paid}
                  onChange={(e) => handleAmountPaidChange(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
                <div className={`w-full px-3 py-2 rounded-lg text-sm font-medium ${
                  formData.payment_status === 'unpaid' ? 'bg-red-100 text-red-800' :
                  formData.payment_status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                  formData.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {formData.payment_status === 'unpaid' && '❌ Unpaid'}
                  {formData.payment_status === 'partial' && `⏳ Partial (€${effectivePrice - formData.amount_paid} remaining)`}
                  {formData.payment_status === 'paid' && '✅ Paid in Full'}
                </div>
              </div>
            </div>
            {effectivePrice > 0 && (
              <div className="mt-3 pt-3 border-t border-blue-200 flex justify-between text-sm">
                <span className="text-blue-700">Total: €{effectivePrice}</span>
                <span className="text-blue-700">Paid: €{formData.amount_paid}</span>
                <span className={formData.amount_paid >= effectivePrice ? "text-green-700 font-medium" : "text-orange-700 font-medium"}>
                  Balance: €{Math.max(0, effectivePrice - formData.amount_paid)}
                </span>
              </div>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Special Requests</label>
            <textarea
              value={formData.special_requests}
              onChange={(e) => setFormData({...formData, special_requests: e.target.value})}
              rows={2}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-teal-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Admin Notes</label>
            <textarea
              value={formData.admin_notes}
              onChange={(e) => setFormData({...formData, admin_notes: e.target.value})}
              rows={2}
              placeholder="e.g., Phone booking, paid cash..."
              className="w-full px-3 py-2 border rounded-lg bg-teal-50 focus:outline-none focus:border-teal-500"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
              {error}
            </div>
          )}
        </form>

        <div className="flex gap-3 p-6 border-t">
          <Button type="submit" onClick={handleSubmit} disabled={saving} className="bg-teal-500 hover:bg-teal-600">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            Create Booking
          </Button>
          <Button onClick={onClose} variant="outline">Cancel</Button>
        </div>
      </div>
    </div>
  );
}
